import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
  useResourceView,
} from "./useCrudResource"

import {
  getAccessoriesStockList,
  getAccessoriesStockById,
  createAccessoriesStock,
  updateAccessoriesStock,
  deleteAccessoriesStock,
  getTrashedAccessoriesStocks,
  restoreAccessoriesStock,
  forceDeleteAccessoriesStock,
  getAccessoriesStockCurrentReport,
} from "api/accessoriesStockApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export const ACCESSORIES_STOCK_QUERY_KEY = "accessories-stocks"

// -------------------- FETCH PAGINATED --------------------
export const useAccessoriesStocks = (params = {}, enabled = true) => {
  return usePaginatedResource(
    ACCESSORIES_STOCK_QUERY_KEY,
    getAccessoriesStockList,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useAccessoriesStock = (id, enabled = true) => {
  return useResourceItem(
    ACCESSORIES_STOCK_QUERY_KEY,
    getAccessoriesStockById,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateAccessoriesStock = () => {
  return useCreateResource(createAccessoriesStock, ACCESSORIES_STOCK_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditAccessoriesStock = () => {
  return useEditResource(updateAccessoriesStock, ACCESSORIES_STOCK_QUERY_KEY)
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteAccessoriesStock = () => {
  return useDeleteResource(deleteAccessoriesStock, ACCESSORIES_STOCK_QUERY_KEY)
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteAccessoriesStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: forceDeleteAccessoriesStock,
    onSuccess: (_, id) => {
      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_STOCK_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(item => item.id !== id)
          updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
          return updated
        }
      )

      // Remove individual cache
      queryClient.removeQueries([ACCESSORIES_STOCK_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all (no pagination)
export const useAllAccessoriesStocks = (params = {}) => {
  return usePaginatedResource(
    ACCESSORIES_STOCK_QUERY_KEY,
    getAccessoriesStockList,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted)
export const useTrashedAccessoriesStocks = (params = {}) => {
  return usePaginatedResource(
    [ACCESSORIES_STOCK_QUERY_KEY, "trashed"],
    getTrashedAccessoriesStocks,
    params,
    true
  )
}

// ✅ Restore trashed item
export const useRestoreAccessoriesStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreAccessoriesStock,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_STOCK_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(item => item.id !== id)
          updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
          return updated
        }
      )

      // Add to main list cache
      queryClient.setQueriesData([ACCESSORIES_STOCK_QUERY_KEY], old => {
        if (!old?.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual cache
      queryClient.setQueryData([ACCESSORIES_STOCK_QUERY_KEY, id], restoredItem)
    },
  })
}

// -------------------- SEARCH (PROGRAMMATIC) --------------------
export const useAccessoriesStockSearchAPI = ({
  all = true,
  limit = 5,
} = {}) => {
  const queryClient = useQueryClient()

  const accessoriesStockSearchAPI = async searchTerm => {
    const queryKey = [
      "accessories-stock-searching",
      { search: searchTerm, all, limit },
    ]
    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getAccessoriesStockList({
          search: searchTerm,
          all: true,
        }),
    })

    return data
  }

  const loading =
    useIsFetching({ queryKey: ["accessories-stock-searching"] }) > 0

  return { accessoriesStockSearchAPI, loading }
}

// -------------------- CURRENT STOCK REPORT --------------------
export const useAccessoriesStockCurrentReport = (enabled = true) => {
  return useResourceView(
    ACCESSORIES_STOCK_QUERY_KEY,
    getAccessoriesStockCurrentReport,
    enabled
  )
}
